import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import selectedCollectionAddressState, {
  SelectedCollectionAddressType,
} from '@/mobile/states/rentalwifi/selectedCollectionAddressState';
import initialCollectionAddressState from '@/mobile/states/rentalwifi/collectionSendBackAddressState';
import collectionAddressForAPIState from '@/mobile/states/rentalwifi/collectionAddressForAPIState';
import agreementCheckState from '@/mobile/states/rentalwifi/agreementCheckState';
import useGetRentalWifiSendBackInfo from '@/mobile/hooks/api/useCollectionAddress';
import { formatPhoneNumber, formatZipCode } from '@/utils/commonFunc';
import CollectionAddressForm, { CollectionAddressFormRef } from './CollectionAddressForm';

// 解約受付API用の集荷先住所データ構造

/**
 * 回収先住所選択コンポーネント
 * レンタルWiFi機器の返却方法（自宅回収/指定住所）を選択させる。
 * 選択に応じて住所入力フォームの表示・非表示を切り替える。
 */
type CollectionAddressData = {
  collection_zip_code: string;
  collection_prefecture: string;
  collection_city: string;
  collection_town: string;
  collection_optional: string;
  collection_tel: string;
  displayAddress: string; // 表示用の整形された住所文字列
};

// 外部から参照可能な関数の型定義
export interface CollectionAddressSelectorRef {
  getSelectedCollectionAddress: () => SelectedCollectionAddressType;
  getSelectedOption: () => 'shipping' | 'custom';
  getCollectionAddressForAPI: () => CollectionAddressData;
  validateForm: (shouldResetAgreement?: boolean) => boolean;
  isValidForSubmission: () => boolean; // 送信可能かどうかを判定する新しい関数
}

// ========================================================================
// メインコンポーネント
// ========================================================================

const CollectionAddress = forwardRef<CollectionAddressSelectorRef, object>((props, ref) => {
  // 戻る操作でも選択状態を維持できるよう、住所種別は Recoil とローカル state の両方で管理する。
  const selectedCollectionData = useRecoilValue(selectedCollectionAddressState);
  const [selectedOption, setSelectedOption] = useState<'shipping' | 'custom'>(
    selectedCollectionData.selectedOption || 'shipping'
  );

  const customFormRef = useRef<CollectionAddressFormRef>(null);
  const [collectionAddressData, setCollectionAddressData] = useState<{
    shipping: CollectionAddressData;
    custom: CollectionAddressData | null;
    isLoading: boolean;
  }>({
    shipping: {
      collection_zip_code: '',
      collection_prefecture: '',
      collection_city: '',
      collection_town: '',
      collection_optional: '',
      collection_tel: '',
      displayAddress: '',
    },
    custom: null,
    isLoading: true,
  });

  const setSelectedCollectionAddress = useSetRecoilState(selectedCollectionAddressState);
  const setCollectionAddressForAPI = useSetRecoilState(collectionAddressForAPIState);
  const setAgreementCheck = useSetRecoilState(agreementCheckState);
  const rentalWifiSendBackInfo = useRecoilValue(initialCollectionAddressState);
  const { execute: getRentalWifiSendBackInfo } = useGetRentalWifiSendBackInfo();

  // 送付先住所候補は API 依存のため初回表示時に取得しておく。
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCollectionAddressData((prev) => ({ ...prev, isLoading: true }));
        await getRentalWifiSendBackInfo();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('送付先住所の取得に失敗:', error);
      } finally {
        setCollectionAddressData((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API 生値を表示用と送信用に再構成し、後続ロジックで同じ形を扱えるようにする。
  useEffect(() => {
    if (!rentalWifiSendBackInfo.deli_zip_code && !rentalWifiSendBackInfo.deli_prefecture) {
      return; // データがない場合は何もしない
    }

    let displayAddress = '';

    // APIから取得したデータで住所文字列を構築
    if (rentalWifiSendBackInfo.deli_zip_code) {
      displayAddress += `${formatZipCode(rentalWifiSendBackInfo.deli_zip_code)} `;
    }
    if (rentalWifiSendBackInfo.deli_prefecture)
      displayAddress += rentalWifiSendBackInfo.deli_prefecture;
    if (rentalWifiSendBackInfo.deli_city) displayAddress += rentalWifiSendBackInfo.deli_city;
    if (rentalWifiSendBackInfo.deli_town) displayAddress += rentalWifiSendBackInfo.deli_town;
    if (rentalWifiSendBackInfo.deli_optional)
      displayAddress += ` ${rentalWifiSendBackInfo.deli_optional}`;

    // 送付先住所データを更新
    const shippingData = {
      collection_zip_code: rentalWifiSendBackInfo.deli_zip_code || '',
      collection_prefecture: rentalWifiSendBackInfo.deli_prefecture || '',
      collection_city: rentalWifiSendBackInfo.deli_city || '',
      collection_town: rentalWifiSendBackInfo.deli_town || '',
      collection_optional: rentalWifiSendBackInfo.deli_optional || '',
      collection_tel: rentalWifiSendBackInfo.deli_tel || '',
      displayAddress,
    };

    setCollectionAddressData((prev) => ({
      ...prev,
      shipping: shippingData,
    }));
  }, [rentalWifiSendBackInfo]);

  // 住所種別切替時に Recoil も同期し、確認画面や戻る操作で選択を失わないようにする。
  const handleOptionChange = (option: 'shipping' | 'custom') => {
    setSelectedOption(option);

    setSelectedCollectionAddress((prev) => ({
      ...prev,
      selectedOption: option,
    }));

    // 入力住所へ切り替えた場合は未検証状態になるため、同意をリセットする。
    if (option === 'custom') {
      setAgreementCheck(false);
    }
  };

  // 手入力フォームの値を API 送信用のスキーマへ変換する。
  const createCustomCollectionData = (currentValues: any): CollectionAddressData => {
    return {
      collection_zip_code: currentValues.zipCode || '',
      collection_prefecture: currentValues.prefecture || '',
      collection_city: currentValues.city || '',
      collection_town: [
        currentValues.district,
        currentValues.street,
        currentValues.unit,
        currentValues.subUnit,
      ]
        .filter(Boolean)
        .join('‐'),
      collection_optional: `${currentValues.building || ''}${
        currentValues.room ? `${currentValues.room}` : ''
      }`,
      collection_tel: currentValues.phone || '',
      displayAddress: `${currentValues.zipCode ? `${formatZipCode(currentValues.zipCode)} ` : ''}${
        currentValues.prefecture || ''
      }${currentValues.city || ''}${currentValues.district || ''}${currentValues.street || ''}${
        currentValues.unit || ''
      }${currentValues.subUnit || ''}${currentValues.building ? ` ${currentValues.building}` : ''}${
        currentValues.room ? ` ${currentValues.room}` : ''
      }`,
    };
  };

  // 画面表示用の住所文字列と API 送信用データを同時に更新し、状態の二重管理崩れを防ぐ。
  const updateRecoilStateForCustom = (currentValues: any, customData: CollectionAddressData) => {
    setSelectedCollectionAddress({
      address: currentValues, // CollectionAddressTypeオブジェクトを保存
      phone: formatPhoneNumber(customData.collection_tel) || '電話番号が設定されていません',
      selectedOption: 'custom',
    });

    setCollectionAddressForAPI({
      collection_zip_code: customData.collection_zip_code,
      collection_prefecture: customData.collection_prefecture,
      collection_city: customData.collection_city,
      collection_town: customData.collection_town,
      collection_optional: customData.collection_optional,
      collection_tel: customData.collection_tel,
    });
  };

  // API 取得済み送付先を選んだ場合も、確認画面用 state と送信用 state を揃えておく。
  const updateRecoilStateForShipping = () => {
    setSelectedCollectionAddress({
      address: collectionAddressData.shipping.displayAddress,
      phone:
        formatPhoneNumber(collectionAddressData.shipping.collection_tel) ||
        '電話番号が設定されていません',
      selectedOption: 'shipping',
    });

    setCollectionAddressForAPI({
      collection_zip_code: collectionAddressData.shipping.collection_zip_code,
      collection_prefecture: collectionAddressData.shipping.collection_prefecture,
      collection_city: collectionAddressData.shipping.collection_city,
      collection_town: collectionAddressData.shipping.collection_town,
      collection_optional: collectionAddressData.shipping.collection_optional,
      collection_tel: collectionAddressData.shipping.collection_tel,
    });
  };

  // ====================================================================
  // useImperativeHandle - 外部アクセス用関数
  // ====================================================================

  useImperativeHandle(ref, () => ({
    getSelectedCollectionAddress: () => {
      if (selectedOption === 'shipping') {
        return {
          address: collectionAddressData.shipping.displayAddress,
          phone:
            formatPhoneNumber(collectionAddressData.shipping.collection_tel) ||
            '電話番号が設定されていません',
        };
      }

      // カスタム住所はフォームが唯一の正本なので、表示用文字列をその場で組み立てる。
      if (customFormRef.current) {
        const currentValues = customFormRef.current.getCurrentValues();
        const displayAddress = `${
          currentValues.zipCode ? `${formatZipCode(currentValues.zipCode)} ` : ''
        }${currentValues.prefecture || ''}${currentValues.city || ''}${
          currentValues.district || ''
        }${currentValues.street || ''}${currentValues.unit || ''}${currentValues.subUnit || ''}${
          currentValues.building ? ` ${currentValues.building}` : ''
        }${currentValues.room ? ` ${currentValues.room}` : ''}`;

        return {
          address: displayAddress || null,
          phone: formatPhoneNumber(currentValues.phone || '') || '電話番号が設定されていません',
        };
      }

      return {
        address: null,
        phone: '電話番号が設定されていません',
      };
    },

    getSelectedOption: () => selectedOption,

    getCollectionAddressForAPI: (): CollectionAddressData => {
      if (selectedOption === 'shipping') {
        return collectionAddressData.shipping;
      }
      return (
        collectionAddressData.custom || {
          collection_zip_code: '',
          collection_prefecture: '',
          collection_city: '',
          collection_town: '',
          collection_optional: '',
          collection_tel: '',
          displayAddress: '',
        }
      );
    },

    validateForm: (shouldResetAgreement = true) => {
      if (selectedOption === 'custom' && customFormRef.current) {
        const isValid = customFormRef.current.validateForm(shouldResetAgreement);

        // 検証済み入力だけを Recoil に反映し、確認画面へ不正値が流れないようにする。
        if (isValid) {
          const currentValues = customFormRef.current.getCurrentValues();
          const customData = createCustomCollectionData(currentValues);
          updateRecoilStateForCustom(currentValues, customData);
        }

        return isValid;
      }

      // 送付先住所利用時は検証不要なので、選択内容の反映だけ行う。
      if (selectedOption === 'shipping') {
        updateRecoilStateForShipping();
      }

      return true; // 送付先住所を使用する場合はバリデーション不要
    },

    isValidForSubmission: () => {
      if (selectedOption === 'custom' && customFormRef.current) {
        const isValid = customFormRef.current.isCurrentlyValid();

        // リアルタイム判定でも有効値だけを反映し、ボタン活性条件と状態を一致させる。
        if (isValid) {
          const currentValues = customFormRef.current.getCurrentValues();
          const customData = createCustomCollectionData(currentValues);
          updateRecoilStateForCustom(currentValues, customData);
        }

        return isValid;
      }
      return true; // 送付先住所を使用する場合は常に有効
    },
  }));

  return (
    <>
      <div>
        <h3>■&nbsp;&nbsp;集荷先住所</h3>
        <br />
      </div>
      <div>
        <table>
          <colgroup>
            <col width='35%' />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan={2} className='collection-address__cell-padding'>
                {/* 既存の送付先をそのまま使う導線を先に出し、再入力が不要なケースを最短にする。 */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className='m-form__lytItem collection-address__radio-label'>
                  <input
                    type='radio'
                    name='collectionOption'
                    value='shipping'
                    className='collection-address__radio-input'
                    onChange={() => handleOptionChange('shipping')}
                    checked={selectedOption === 'shipping'}
                  />
                  送付先住所で集荷を行う
                </label>
              </td>
            </tr>
            {selectedOption === 'shipping' && (
              <tr>
                <td className='collection-address__cell-padding'>
                  {collectionAddressData.isLoading ? (
                    // API 取得中に空欄を見せるより、読み込み中であることを明示する。
                    <div className='collection-address__address-info'>住所を取得中...</div>
                  ) : (
                    <>
                      <div className='collection-address__address-info'>
                        住所: {collectionAddressData.shipping.displayAddress}
                      </div>
                      <div className='collection-address__phone-info'>
                        電話番号:{' '}
                        {formatPhoneNumber(collectionAddressData.shipping.collection_tel) ||
                          '電話番号が設定されていません'}
                      </div>
                    </>
                  )}
                </td>
              </tr>
            )}
            <tr>
              <td colSpan={2} className='collection-address__cell-padding'>
                {/* 送付先以外で受け取りたい利用者向けに手入力導線も提供する。 */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className='m-form__lytItem collection-address__radio-label'>
                  <input
                    type='radio'
                    name='collectionOption'
                    value='custom'
                    className='collection-address__radio-input'
                    onChange={() => handleOptionChange('custom')}
                    checked={selectedOption === 'custom'}
                  />
                  送付先住所から変更する
                </label>
              </td>
            </tr>
            {selectedOption === 'custom' && (
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td colSpan={2}>
                  <CollectionAddressForm ref={customFormRef} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
});

// displayNameを設定
CollectionAddress.displayName = 'CollectionAddress';

export default CollectionAddress;
